import { getDisplayText } from './display'

function isGoogleDriveUrl(rawText: string): boolean {
  try {
    const host = new URL(rawText).hostname.toLowerCase().replace(/^www\./, '')
    return (
      host === 'drive.google.com' ||
      host === 'drive.usercontent.google.com' ||
      host === 'docs.google.com'
    )
  } catch {
    return false
  }
}

export function buildTransactionSlipImageUrl(recordId: unknown, workspaceId: unknown): string | null {
  const id = getDisplayText(recordId).trim()
  const ws = getDisplayText(workspaceId).trim()
  if (!id || !ws) return null

  const params = new URLSearchParams({ workspace_id: ws })
  return `/api/v1/transactions/${encodeURIComponent(id)}/image?${params.toString()}`
}

function getRecordImageFieldValue(
  record: Record<string, unknown>,
  columnValue?: unknown,
): string {
  return getDisplayText(record.image_url ?? columnValue).trim()
}

function isDirectResolvableImageUrl(value: string): boolean {
  if (!value) return false
  if (/^https?:\/\//i.test(value)) return true
  if (value.startsWith('/api/')) return true
  if (/^data:/i.test(value) || /^blob:/i.test(value)) return true
  return false
}

export function resolveTransactionImageCandidates(input: {
  record: Record<string, unknown>
  workspaceId?: string | null
  columnValue?: unknown
}): string[] {
  const ws = getDisplayText(input.workspaceId).trim()
  const candidates: string[] = []
  const seen = new Set<string>()

  const pushResolved = (raw: unknown) => {
    const resolved = resolveTableImageUrl({
      rawUrl: raw,
      record: input.record,
      workspaceId: ws || null,
    })
    if (!resolved || seen.has(resolved)) return
    seen.add(resolved)
    candidates.push(resolved)
  }

  const fieldUrl = getRecordImageFieldValue(input.record, input.columnValue)
  const recordId = getDisplayText(input.record.record_id).trim()
  const internalId = getDisplayText(input.record.id).trim()

  if (fieldUrl && isDirectResolvableImageUrl(fieldUrl)) {
    pushResolved(fieldUrl)
  }

  if (ws) {
    if (recordId) pushResolved(buildTransactionSlipImageUrl(recordId, ws))
    if (internalId && internalId !== recordId) {
      pushResolved(buildTransactionSlipImageUrl(internalId, ws))
    }
  }

  return candidates
}

export function resolveTableImageUrl(input: {
  rawUrl: unknown
  record?: Record<string, unknown> | null
  workspaceId?: string | null
}): string | null {
  const rawText = getDisplayText(input.rawUrl).trim()
  if (!rawText) return null

  if (/^https?:\/\//i.test(rawText) || /^data:/i.test(rawText) || /^blob:/i.test(rawText)) {
    // Google Drive view links often need server-side auth/cookies before they can be shown as images.
    if (isGoogleDriveUrl(rawText)) {
      return `/api/integrations/google/drive-file?driveUrl=${encodeURIComponent(rawText)}`
    }
    return rawText
  }

  try {
    const base = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost'
    const url = new URL(rawText, base)
    const hasWorkspaceId = url.searchParams.has('workspace_id') || url.searchParams.has('workspaceId')

    if (!hasWorkspaceId) {
      const fromRecord = input.record?.workspace_id ?? input.record?.workspaceId ?? input.record?.workspace
      const fromQuery = typeof window !== 'undefined'
        ? new URL(window.location.href).searchParams.get('workspace_id') ??
          new URL(window.location.href).searchParams.get('workspaceId')
        : null
      const workspaceId = getDisplayText(input.workspaceId ?? fromRecord ?? fromQuery).trim()
      if (workspaceId) url.searchParams.set('workspace_id', workspaceId)
    }

    return url.origin === base ? `${url.pathname}${url.search}` : url.toString()
  } catch {
    return rawText
  }
}
